import { MessageCircle, MapPin, Clock, Calendar } from "lucide-react";
import { CLINIC, MEDIA, buildWaUrl } from "@/lib/data";

const Badge = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 backdrop-blur-md border border-white/70 text-[12px] sm:text-[13px] font-medium text-slate-700 shadow-sm"
  >
    {children}
  </div>
);

export default function Hero() {
  const dentalWa = buildWaUrl(
    CLINIC.dental.waNumber,
    "Bună ziua! Doresc o programare la Marimar Dental Clinic."
  );
  const fizioWa = buildWaUrl(
    CLINIC.fizio.waNumber,
    "Bună ziua! Doresc o programare la Marimar Fizio & Kinetoterapie."
  );

  return (
    <section
      id="acasa"
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-[72px] lg:pt-[80px] overflow-hidden"
    >
      {/* Split video background */}
      <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
        <div className="relative overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={MEDIA.dentalVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid="hero-video-dental"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/25 via-transparent to-white/40" />
        </div>
        <div className="relative overflow-hidden hidden md:block">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={MEDIA.fizioVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid="hero-video-fizio"
          />
          <div className="absolute inset-0 bg-gradient-to-bl from-brand-aqua/25 via-transparent to-white/40" />
        </div>
      </div>

      {/* Soft white overlay for readability */}
      <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px]" aria-hidden="true" />

      {/* Decorative blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand-blue/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-aqua/10 blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-12 lg:py-20 min-h-[calc(100svh-80px)] flex items-center">
        <div className="w-full">
          <div className="max-w-4xl mx-auto text-center mb-10 lg:mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/85 backdrop-blur-md border border-white/70 shadow-sm mb-7 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-brand-aqua animate-pulse" />
              <span className="text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-blue">
                Clinică medicală premium · București
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-display text-[40px] leading-[1.05] sm:text-[56px] lg:text-[72px] font-light text-slate-900 text-balance"
            >
              Marimar Clinic — <span className="text-gradient-blue font-medium">sănătate</span>,{" "}
              <span className="font-medium" style={{ color: "#9DC83A" }}>zâmbet</span> și recuperare medicală într-un singur loc.
            </h1>
            <p className="mt-6 lg:mt-8 text-[16px] lg:text-[19px] leading-relaxed text-slate-700 max-w-3xl mx-auto text-balance">
              Stomatologie modernă pentru adulți și copii, fizioterapie și kinetoterapie
              personalizată, într-o clinică din București unde profesionalismul, tehnologia
              și grija pentru pacient se întâlnesc.
            </p>

            <div className="mt-9 lg:mt-11 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch">
              <a
                href={dentalWa}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 bg-brand-blue text-white font-medium text-[15px] lg:text-[16px] px-7 py-4 rounded-full hover:bg-brand-blueDark transition-all shadow-xl shadow-brand-blue/25 hover:shadow-2xl hover:shadow-brand-blue/40 hover:-translate-y-0.5"
                data-testid="hero-cta-dental"
              >
                <MessageCircle className="w-5 h-5 group-hover:rotate-[-8deg] transition-transform" />
                <span>Programează la Dental</span>
                <span className="hidden sm:inline text-white/80 text-[13px] pl-1 border-l border-white/30 ml-1">
                  0737 859 988
                </span>
              </a>
              <a
                href={fizioWa}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 bg-brand-aqua text-white font-medium text-[15px] lg:text-[16px] px-7 py-4 rounded-full hover:bg-brand-aquaDark transition-all shadow-xl shadow-brand-aqua/25 hover:shadow-2xl hover:shadow-brand-aqua/40 hover:-translate-y-0.5"
                data-testid="hero-cta-fizio"
              >
                <MessageCircle className="w-5 h-5 group-hover:rotate-[-8deg] transition-transform" />
                <span>Programează la Fizio</span>
                <span className="hidden sm:inline text-white/80 text-[13px] pl-1 border-l border-white/30 ml-1">
                  0737 859 911
                </span>
              </a>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-4xl mx-auto">
            <Badge testId="hero-badge-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-blue" />
              Stomatologie adulți și copii
            </Badge>
            <Badge testId="hero-badge-2">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-aqua" />
              Fizioterapie & kinetoterapie
            </Badge>
            <Badge testId="hero-badge-3">
              <Clock className="w-3.5 h-3.5 text-brand-blue" />
              L–V 09:00–19:00
            </Badge>
            <Badge testId="hero-badge-4">
              <Calendar className="w-3.5 h-3.5 text-brand-blue" />
              Sâmbătă 09:00–12:00
            </Badge>
            <Badge testId="hero-badge-5">
              <MapPin className="w-3.5 h-3.5 text-brand-blue" />
              Șos. Andronache 52-54, Sector 2
            </Badge>
          </div>
        </div>
      </div>

      {/* Bottom fade to white */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
